import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { DEFAULT_FOLDER_ID } from '../../constants'
import { useGetGoalRecurringTemplateLinks } from '../../services/api/goals.hooks'
import { useRecurringTaskTemplates } from '../../services/api/recurring-tasks.hooks'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import CreateTaskDialog from '../tasks/CreateTaskDialog'
import AchieveGoalDialog from './AchieveGoalDialog'
import ArchiveGoalDialog from './ArchiveGoalDialog'
import GoalRedraftDialog from './GoalRedraftDialog'
import { activeRecurringTemplates } from './RecurringGoalHooks'
import RepairDialog from './RepairDialog'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { linkedTasksForGoal } from './goalTasks'
import { TGoal } from './goalTypes'

interface GoalPlanDialogsProps {
    goal: TGoal
    isCreateOpen: boolean
    onCreateOpenChange: (open: boolean) => void
    isRedraftOpen: boolean
    onRedraftOpenChange: (open: boolean) => void
    /** The new task's optimistic id, as soon as it is created. */
    onTaskCreated?: (optimisticId: string) => void
    onGoalLinkFailed?: (taskId: string, goalId: string) => void
}

/**
 * The dialogs that open over a goal's details pane: New task, Redraft, Repair and Archive. They are
 * mounted once here because two panes need them — GoalDetails and the weekly review that takes its
 * place at /goals/review — and a second copy of Redraft's accept handling is how one of them would
 * end up dropping the plan's one-off tasks.
 */
const GoalPlanDialogs = ({
    goal,
    isCreateOpen,
    onCreateOpenChange,
    isRedraftOpen,
    onRedraftOpenChange,
    onTaskCreated,
    onGoalLinkFailed,
}: GoalPlanDialogsProps) => {
    const { taskGoalLinks, createLinkedTask, updateGoal, isSavingGoal } = useGoalCreation()
    const { data: tasks } = useGetTasksV4()
    const { data: recurringTemplates } = useRecurringTaskTemplates()
    const { data: recurringTemplateLinks = {} } = useGetGoalRecurringTemplateLinks()

    const linked = useMemo(() => linkedTasksForGoal(goal.id, tasks, taskGoalLinks), [goal.id, tasks, taskGoalLinks])
    const repeatTitles = useMemo(
        () =>
            activeRecurringTemplates(recurringTemplates)
                .filter((template) => (recurringTemplateLinks[template.id] ?? []).includes(goal.id))
                .map((template) => template.title),
        [goal.id, recurringTemplateLinks, recurringTemplates]
    )

    /**
     * An accepted plan's one-offs become linked tasks, the same way creation handles them —
     * otherwise accepting a redraft would silently drop them, since one-offs are never
     * contributors. Titles already linked to this goal are skipped: Redraft can be run any
     * number of times and a revised plan usually repeats most of its items.
     */
    const addPlanTasks = (titles: string[]) => {
        const existing = new Set(linked.map((t) => t.title.trim().toLowerCase()))
        titles.forEach((title) => {
            if (existing.has(title.trim().toLowerCase())) return
            existing.add(title.trim().toLowerCase())
            createLinkedTask(title, goal.id)
        })
    }

    return (
        <>
            <CreateTaskDialog
                isOpen={isCreateOpen}
                onOpenChange={onCreateOpenChange}
                folderId={DEFAULT_FOLDER_ID}
                goalId={goal.id}
                onCreated={onTaskCreated}
                onGoalLinkFailed={onGoalLinkFailed}
            />
            <GoalRedraftDialog
                goal={goal}
                repeatTitles={repeatTitles}
                existingTaskTitles={linked.map((task) => task.title)}
                open={isRedraftOpen}
                onOpenChange={onRedraftOpenChange}
                onAccept={(patch, planTaskTitles) => {
                    updateGoal(goal.id, patch, {
                        onSuccess: () => {
                            addPlanTasks(planTaskTitles)
                            toast.success('Plan updated')
                            onRedraftOpenChange(false)
                        },
                    })
                }}
                isSaving={isSavingGoal}
            />
            <RepairDialog />
            <ArchiveGoalDialog />
            <AchieveGoalDialog />
        </>
    )
}

export default GoalPlanDialogs
